import type { InvoiceStatus } from "@/lib/api/types";

/** The till's two narrow-width panes. Only one is on screen below `lg`. */
export type TillPane = "menu" | "bill";

/**
 * WHICH PANE A 390px TILL OPENS ON (UX-round D18, ruling E18).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * The screen used to open on the menu for every status — a one-line `useState`
 * initialiser — so a CLOSED cheque waiting to be paid and a SETTLED bill both
 * greeted a cashier with a grid of dishes, and the itemised bill and the
 * totals she had come for were behind a tap she had to know to make. Taking
 * money is most of her night; ordering is the waiter's screen.
 *
 * So the pane answers the bill's state:
 *
 * - `OPEN` — the reason to open this screen is to put food on the bill, and
 *   the summary bar carries the figures the whole time anyway.
 * - `CLOSED` — the cheque is out and the next act is Settle, which lives on
 *   the bill pane with the lines the guest is about to query.
 * - `SETTLED` — the money is in; what is left is reading, reprinting or
 *   refunding, all of which are on the bill.
 * - `CANCELLED` — nothing can be ordered onto it ever again. A dish grid over
 *   a dead bill is an invitation the screen cannot honour.
 *
 * A pure function, not an inline ternary, because a `useState` initialiser
 * nothing pins is exactly what gets "tidied" back (guard M8, and the house
 * preference at `apps/restaurant/AGENTS.md:171` with `tillActions.ts` as the
 * model). This is the person's STARTING pane only: the summary-bar toggle
 * overrides it for the life of the visit and is never overruled by it.
 */
export function initialPane(status: InvoiceStatus): TillPane {
  // An OPEN bill opens on the menu even when it already has an order (founder,
  // 2026-10-28, reversing #332's bill-first rule): the waiter came to add a
  // round, and the lines are one tap away on the bar.
  return status === "OPEN" ? "menu" : "bill";
}
